<template>
    <div class="voucher-html-editor" v-if="editor">
        <div class="html-editor">
            <EditorToolbar :editor="editor" />
            <EditorContent :editor="editor" ref="editorContentElement" />
        </div>
        <EditorPageSettings
            class="editor-settings"
            :editor="editor"
            :locationId="props.locationId"
            :locationDetail="props.locationDetail"
            v-model:editorBackgroundColor="editorBackground"
            v-model:editorBackgroundImage="editorBackgroundImage"
            v-model:editorBackgroundImageId="editorBackgroundImageId"
            v-model:editorPadding="editorPadding"
            ref="editorPageSettings"
        />
    </div>
</template>

<script setup lang="ts">
    import type { ILocationDetail } from "@/data/model/Location";
    import { i18n } from "@/portal/Trans";
    import { UiNotification } from "@/portal/ui/Notification";
    import { logInformation } from "@/shared/logging/informations";
    import { Color } from "@tiptap/extension-color";
    import { Image } from "@tiptap/extension-image";
    import { TextAlign } from "@tiptap/extension-text-align";
    import { TextStyle } from "@tiptap/extension-text-style";
    import { StarterKit } from "@tiptap/starter-kit";
    import { EditorContent, useEditor } from "@tiptap/vue-3";
    import { onBeforeUnmount, onMounted, provide, ref, watch } from "vue";

    import EditorPageSettings from "./EditorPageSettings.vue";
    import EditorToolbar from "./EditorToolbar.vue";

    import { AbsoluteTextBox, AbsoluteTextBoxEvents } from "./extensions/AbsoluteTextBox";
    import { DocumentBackgroundColor } from "./extensions/DocumentBackgroundColorExtension";
    import { DocumentImage } from "./extensions/DocumentImageExtension";
    import { DocumentImageVariable } from "./extensions/DocumentImageVariableExtension";
    import { DocumentVariable } from "./extensions/DocumentVariableExtension";
    import { DocumentWrapperService } from "./helpers/DocumentWrapperService";
    import { useEditableAreaStyle } from "./helpers/useEditableAreaStyle";

    const model = defineModel<string>();

    const props = defineProps<{
        locationId: string;
        locationDetail: ILocationDetail | undefined;
    }>();

    const editorBackground = ref<string>("#ffffff");
    const editorBackgroundImage = ref<string | null>(null);
    const editorBackgroundImageId = ref<string | null>(null);
    const editorPadding = ref<string>("1em");
    const editorStyle = ref<Record<string, string>>({}); // width, height, padding, etc.
    const editorContentElement = ref<HTMLElement | null>(null);
    const editorPageSettings = ref<InstanceType<typeof EditorPageSettings>>();

    editorStyle.value = {
        width: "794px",
        height: "1123px",
        padding: editorPadding.value,
    };

    const editor = useEditor({
        content: "",
        editable: false,
        extensions: [
            StarterKit,
            AbsoluteTextBox,
            Color,
            TextStyle,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            DocumentBackgroundColor,
            DocumentVariable,
            DocumentImageVariable,
            DocumentImage,
            Image,
        ],
        onUpdate: () => updateFinalHtml(),
    });

    useEditableAreaStyle({
        editor: editor,
        editorContentElement: editorContentElement,
        style: editorStyle,
        backgroundColor: editorBackground,
        backgroundImage: editorBackgroundImage,
        padding: editorPadding,
    });

    watch(
        [editorStyle, editorBackground, editorBackgroundImage, editorPadding],
        () => {
            updateFinalHtml();
        },
        { deep: true }
    );

    function updateFinalHtml() {
        const html = DocumentWrapperService.build({
            html: editor.value?.getHTML() ?? "",
            backgroundColor: editorBackground.value,
            backgroundImage: editorBackgroundImage.value ?? undefined,
            backgroundImageId: editorBackgroundImageId.value ?? undefined,
            padding: editorPadding.value,
            width: editorStyle.value.width,
            height: editorStyle.value.height,
        });
        logInformation(html);
        model.value = html;
    }

    function validateRequiredVariablesAndImages() {
        const requiredVariablesAndImages = editorPageSettings.value?.getRequiredVariablesAndImages() ?? [];
        const finalHtml = model.value ?? "";

        const variablesNotPresent: string[] = [];
        for (const variable of requiredVariablesAndImages) {
            if (!finalHtml.includes(`data-variable-name="${variable}"`)) {
                variablesNotPresent.push(variable);
            }
        }

        if (variablesNotPresent.length > 0) {
            UiNotification.showException(
                i18n("voucherDesign.variablesNotPresent", { variables: variablesNotPresent.join(", ") })
            );
            return false;
        }

        return true;
    }

    function updateModel(html: string) {
        const parsed = DocumentWrapperService.parse(html);

        editorBackground.value = parsed.backgroundColor ?? "#ffffff";
        editorBackgroundImage.value = parsed.backgroundImage ?? null;
        editorBackgroundImageId.value = parsed.backgroundImageId ?? null;
        editorPadding.value = parsed.padding ?? "1em";

        editorStyle.value = {
            width: parsed.width ?? "794px",
            height: parsed.height ?? "1123px",
            padding: editorPadding.value,
        };

        editor.value?.commands.setContent(parsed.html);

        updateFinalHtml();
    }

    onBeforeUnmount(() => {
        editor.value?.destroy();
    });

    const clickedInBox = ref<Element | null>(null);
    const isFocusRecentlyLost = ref<boolean>(false);
    provide("isFocusRecentlyLost", isFocusRecentlyLost);

    onMounted(() => {
        document.addEventListener("mousedown", (e) => {
            const newClickedInBox = (e.target as HTMLElement).closest(".absolute-text-box");
            if (clickedInBox.value && newClickedInBox != clickedInBox.value) {
                isFocusRecentlyLost.value = true;
                window.setTimeout(() => {
                    isFocusRecentlyLost.value = false;
                }, 100);
            }

            clickedInBox.value = newClickedInBox;
        });

        AbsoluteTextBoxEvents.delete.add(() => {
            clickedInBox.value = null;
        });
    });

    defineExpose({
        validateRequiredVariablesAndImages,
        updateModel,
    });
</script>

<style lang="scss">
    @use "./../../../../../assets/styles/scss/shared/colors.scss";
    @use "./../../../../../assets/styles/scss/shared/variables.scss";

    .voucher-html-editor {
        display: flex;
        flex-direction: row;
        border: 1px solid #ebedf2;
        border-radius: 0.5rem;

        .html-editor {
            padding: 1rem;
            flex: 1;

            & > h4 {
                margin-bottom: 2rem;
                font-size: 1.2rem;
                font-weight: 500;
                color: #48465b;
            }

            img {
                &.ProseMirror-selectednode {
                    outline: 3px solid var(--purple);
                }
            }

            .tiptap {
                width: 796px !important; // 794px for a4 size + 2px for border
                height: 1125px !important; // 1123px for a4 size + 2px for border
                min-height: 1122px; // a4 with 96DPI size
                border: 1px solid #ccc;
                border-radius: 0.3rem;
                padding: 1rem;
                overflow: hidden;
            }

            .variable-tag {
                margin: -1px;
                border: 1px dashed #ccc;
                border-radius: 5px;
            }

            .absolute-text-box {
                position: absolute;
                border: 1px solid rgba(204, 204, 204, 0.3);
                border-radius: 4px;
                padding: 7px;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
                transition:
                    border-color 0.2s ease-in-out,
                    box-shadow 0.2s ease-in-out;

                &:hover {
                    border-color: rgba(204, 204, 204, 1);
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

                    .drag-handle {
                        opacity: 1;
                    }
                }

                .drag-handle {
                    position: absolute;
                    top: -1px;
                    left: -18px;
                    width: 20px;
                    height: 20px;
                    cursor: grab;
                    user-select: none;
                    z-index: 10;
                    pointer-events: auto; // Ensure handle can receive mouse events
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    opacity: 0.4;
                    transition: opacity 0.2s ease-in-out;

                    &.dragging {
                        cursor: grabbing;
                    }

                    &::after {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 14px;
                        height: 20px;
                        content: "⠿";
                        font-weight: 700;
                        cursor: grab;
                        background: #f0f0f0;
                        color: #a9a9a9;
                        border-top-left-radius: 3px;
                        border-bottom-left-radius: 3px;
                    }
                }

                .remove-button {
                    position: absolute;
                    top: -1px;
                    right: -16px;
                    width: 16px;
                    height: 16px;
                    border: 1px solid rgba(204, 204, 204, 1);
                    background-color: rgba(240, 240, 240, 1);
                    color: #a9a9a9;
                    font-weight: 400;
                    font-size: 14px;
                    border-top-right-radius: 3px;
                    border-bottom-right-radius: 3px;
                    border-left: 0;
                    opacity: 0.5;
                    cursor: pointer;
                    z-index: 20;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    transition: opacity 0.2s ease-in-out;
                    user-select: none;

                    &:hover {
                        opacity: 1;
                    }
                }

                .resize-handle {
                    position: absolute;
                    bottom: -1px;
                    right: -16px;
                    width: 16px;
                    height: 16px;
                    border: 1px solid rgba(204, 204, 204, 1);
                    background-color: rgba(240, 240, 240, 1);
                    color: #a9a9a9;
                    font-size: 14px;
                    font-weight: 400;
                    cursor: se-resize;
                    z-index: 20;
                    border-top-right-radius: 3px;
                    border-bottom-right-radius: 3px;
                    border-left: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    opacity: 0.5;
                    transition: opacity 0.2s ease-in-out;
                    user-select: none;

                    &::after {
                        content: "↘";
                        line-height: 1;
                    }

                    &:hover {
                        opacity: 1;
                    }
                }

                .content {
                    min-height: 1em;
                    outline: none;
                    pointer-events: auto; // Allow text editing
                }

                // When dragging, show visual feedback
                &.dragging {
                    opacity: 0.8;
                    z-index: 1000;
                }
            }
        }
    }

    .html-editor-preview {
        .absolute-text-box {
            padding: 8px;
        }
    }

    .html-editor .tiptap,
    .html-editor-preview {
        font-family: variables.$fontFamily;
        color: colors.$black;
        font-size: 14px;
        -webkit-font-smoothing: none;
        white-space-collapse: break-spaces;
        text-decoration-thickness: 1px;
        text-underline-offset: 2px;
        position: relative;

        .document {
            position: relative;
            overflow: hidden;
        }

        p {
            line-height: 1.5;
            margin: 0 0 14px;
            font-weight: 300;
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            font-weight: 500;
            line-height: 1.2;
            text-wrap: pretty;
        }

        h1 {
            font-size: 32px;
            margin: 0 0 16px;
        }

        h2 {
            font-size: 24px;
            margin: 0 0 12px;
        }

        h3 {
            font-size: 20px;
            margin: 0 0 10px;
        }

        h4 {
            font-size: 18px;
            margin: 0 0 9px;
        }

        h5 {
            font-size: 16px;
            margin: 0 0 8px;
        }

        h6 {
            font-size: 15px;
            margin: 0 0 7.5px;
        }

        img {
            display: inline-block;
            height: auto;
            max-width: 100%;
        }

        .variable-tag {
            pointer-events: none;
            user-select: all;
        }

        .variable-image {
            display: inline-block;
            object-fit: contain;
        }

        hr {
            border: none;
            border-top: 1px solid #a0a0a0;
            margin: 20px 0;
        }

        ul,
        ol {
            padding: 0 14px;
            margin: 0 14px 18px 21px;

            li p {
                margin-top: 3px;
                margin-bottom: 3px;
            }
        }

        strong {
            font-weight: 700;
        }

        u {
            text-decoration-thickness: 1px !important;
            text-underline-offset: 2px !important;
        }
    }
</style>
